const campaignValue = (campaign, ...keys) => {
    for (const key of keys) {
        const value = campaign?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return String(value).trim();
        }
    }

    return "";
};

export function campaignFilterKey(campaign) {
    const integrationId = campaignValue(campaign, "integrationId");
    const campaignId = campaignValue(campaign, "campaignId");
    if (!integrationId || !campaignId) return "";
    return `campaign:${encodeURIComponent(integrationId)}:${encodeURIComponent(campaignId)}`;
}

export function campaignForFilterRegistry(campaign) {
    const key = campaignFilterKey(campaign);
    if (!key) return campaign;

    return {
        ...campaign,
        campaignRemoteId: campaignValue(campaign, "campaignRemoteId", "remoteId"),
        remoteId: key,
    };
}

function legacyCampaignAliasMatchesOnlyThisCampaign(campaign, legacyRemoteId, registeredCampaigns) {
    const matchingCampaignKeys = new Set([...registeredCampaigns, campaign]
        .filter((candidate) => campaignValue(candidate, "campaignRemoteId", "remoteId") === legacyRemoteId)
        .map((candidate) => campaignFilterKey(candidate) || campaignChartIdentity(candidate))
        .filter(Boolean));
    const campaignKey = campaignFilterKey(campaign) || campaignChartIdentity(campaign);

    return matchingCampaignKeys.size === 1 && matchingCampaignKeys.has(campaignKey);
}

export function campaignMatchesFilter(campaign, allowListed, registeredCampaigns = []) {
    if (!Array.isArray(allowListed) || allowListed.length === 0) return true;

    const key = campaignFilterKey(campaign);
    if (key && allowListed.includes(key)) return true;

    const legacyRemoteId = campaignValue(campaign, "campaignRemoteId", "remoteId");
    if (!legacyRemoteId || !allowListed.includes(legacyRemoteId)) return false;

    return legacyCampaignAliasMatchesOnlyThisCampaign(campaign, legacyRemoteId, registeredCampaigns);
}

export function campaignIsSelectedByFilter(campaign, allowListed, registeredCampaigns = []) {
    if (!Array.isArray(allowListed) || allowListed.length === 0) return false;

    const key = campaignFilterKey(campaign);
    if (key && allowListed.includes(key)) return true;

    const legacyRemoteId = campaignValue(campaign, "campaignRemoteId", "remoteId");
    if (!legacyRemoteId || !allowListed.includes(legacyRemoteId)) return false;

    return legacyCampaignAliasMatchesOnlyThisCampaign(campaign, legacyRemoteId, registeredCampaigns);
}

export function campaignChartIdentity(campaign) {
    const integration = campaignValue(campaign, "integrationId", "integrationName");
    const campaignId = campaignValue(campaign, "campaignId");
    const remoteId = campaignValue(campaign, "campaignRemoteId", "remoteId");
    const campaignKey = campaignId || remoteId || campaignValue(campaign, "campaignName");

    return [integration, campaignKey].filter(Boolean).join("::");
}

export function campaignChartColors(campaign) {
    const identity = campaignChartIdentity(campaign) || campaignValue(campaign, "campaignName") || "unknown";
    let hash = 2166136261;

    for (let index = 0; index < identity.length; index++) {
        hash ^= identity.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    const hue = (hash >>> 0) % 360;
    const saturation = 62 + ((hash >>> 8) % 13);
    const lightness = 42 + ((hash >>> 16) % 11);
    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

    return {
        borderColor: color,
        backgroundColor: `hsla(${hue}, ${saturation}%, ${lightness}%, 0.8)`,
    };
}

export function campaignOrderSeries(campaign, isMarketing) {
    return isMarketing ? campaign?.dataGross : campaign?.dataNet;
}

export function campaignGraphForSnapshot(graph, snapshot) {
    if (!graph || !isSnapshotPeriodIncomplete(snapshot)) return graph;
    if (!snapshotHasPublishedDays(snapshot)) {
        return {...graph, labels: [], dataSources: []};
    }

    const labels = withSnapshotMissingDays(snapshot, graph.labels ?? []);
    const fillMissingDays = (dailyValues) => Object.fromEntries(labels.map((date) => [
        date,
        (snapshot.missingDays ?? []).includes(date) ? null : dailyValues?.[date],
    ]));

    return {
        ...graph,
        labels,
        dataSources: (graph.dataSources ?? []).map((source) => ({
            ...source,
            ...(source.dataNet !== undefined ? {dataNet: fillMissingDays(source.dataNet)} : {}),
            ...(source.dataGross !== undefined ? {dataGross: fillMissingDays(source.dataGross)} : {}),
            ...(source.data !== undefined ? {data: fillMissingDays(source.data)} : {}),
        })),
    };
}

export function campaignCpcSeries(campaign) {
    return Object.fromEntries(Object.entries(campaign?.data ?? {}).map(([day, value]) => {
        const amount = value?.amount;
        if (amount === null || amount === undefined || amount === "") return [day, null];
        const numericAmount = Number(amount);
        return [day, Number.isFinite(numericAmount) ? numericAmount / 100 : null];
    }));
}

export function campaignCpcYAxisOptions({tickColor, gridColor}) {
    return {
        type: "linear",
        min: 0,
        border: {display: false},
        ticks: {color: tickColor},
        grid: {color: gridColor},
    };
}

export function campaignPnoYAxisOptions({tickColor, gridColor}) {
    return {
        type: "linear",
        min: 0,
        border: {display: false},
        ticks: {maxTicksLimit: 7, color: tickColor},
        grid: {color: gridColor},
    };
}

export function campaignChartDisplayLabels(campaigns) {
    const nameCounts = new Map();
    for (const campaign of campaigns) {
        const name = campaignValue(campaign, "campaignName", "name") || "";
        const normalizedName = name.toLowerCase();
        nameCounts.set(normalizedName, (nameCounts.get(normalizedName) ?? 0) + 1);
    }

    const qualifiedLabels = campaigns.map((campaign) => {
        const name = campaignValue(campaign, "campaignName", "name") || "";
        const normalizedName = name.toLowerCase();
        const duplicateName = (nameCounts.get(normalizedName) ?? 0) > 1;
        const source = campaignValue(campaign, "integrationName");
        const remoteId = campaignValue(campaign, "campaignRemoteId", "remoteId");
        const distinctSource = source.toLowerCase() === name.toLowerCase() ? "" : source;
        const qualifier = duplicateName ? [distinctSource, remoteId].filter(Boolean).join(" · ") : "";

        return {campaign, name, duplicateName, qualifier};
    });
    const qualifiedCounts = new Map();
    for (const {name, qualifier} of qualifiedLabels) {
        const key = `${name.toLowerCase()}::${qualifier.toLowerCase()}`;
        qualifiedCounts.set(key, (qualifiedCounts.get(key) ?? 0) + 1);
    }

    const labels = new Map();
    for (const {campaign, name, duplicateName, qualifier} of qualifiedLabels) {
        if (!duplicateName) {
            labels.set(campaignChartIdentity(campaign), name);
            continue;
        }

        const campaignId = campaignValue(campaign, "campaignId");
        const qualifierKey = `${name.toLowerCase()}::${qualifier.toLowerCase()}`;
        const uniqueQualifier = (qualifiedCounts.get(qualifierKey) ?? 0) > 1
            ? [qualifier, campaignId].filter(Boolean).join(" · ")
            : qualifier;
        labels.set(campaignChartIdentity(campaign), uniqueQualifier ? `${name} (${uniqueQualifier})` : name);
    }

    return labels;
}
import {
    isSnapshotPeriodIncomplete,
    snapshotHasPublishedDays,
    withSnapshotMissingDays,
} from "../../../../../utils/reportingSnapshotAvailability.js";

